import { Box, Flex, type FlexProps, Icon } from "@chakra-ui/react";
import { type IconType } from "react-icons";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { isBrowser } from "react-device-detect";
import { type PropsWithChildren, useMemo, useState } from "react";

export interface LinkItemProps {
  name: string;
  icon: IconType;
  path?: string;
  external?: boolean;
  children?: LinkItemProps[];
}

// Determine if a given path is active for the current location (internal links only)
const isPathActive = (
  currentPathname: string,
  path?: string,
  external?: boolean,
) => {
  if (external || typeof path !== "string") return false;
  return path === "/"
    ? currentPathname === "/"
    : currentPathname.startsWith(path);
};

// Recursively determine if any descendant (or immediate child) is active
const hasActiveDescendant = (
  node: LinkItemProps,
  currentPathname: string,
): boolean => {
  if (!node.children || node.children.length === 0) return false;
  return node.children.some(
    (child) =>
      isPathActive(currentPathname, child.path, child.external) ||
      hasActiveDescendant(child, currentPathname),
  );
};

export const LinkItem = ({
  link,
  onClose,
}: {
  link: LinkItemProps;
  onClose: () => void;
}) => {
  const location = useLocation();
  const isChildActive = useMemo(
    () => hasActiveDescendant(link, location.pathname),
    [link, location.pathname],
  );
  const [isOpen, setIsOpen] = useState(() => isChildActive);

  // If this node has children, render a toggleable header and its recursive children
  if (link.children && link.children.length > 0) {
    return (
      <Box key={link.name}>
        <Box
          onClick={() => setIsOpen((v) => !v)}
          as={"button"}
          style={{ textDecoration: "none", width: "100%" }}
        >
          <Flex
            align="center"
            p={!isBrowser ? 4 : 3}
            borderRadius="lg"
            role="group"
            cursor="pointer"
            bg={isChildActive ? "blue.900" : undefined}
            color={isChildActive ? "white" : undefined}
            _hover={{ bg: "orange.600", color: "black" }}
          >
            <Icon
              mr="4"
              fontSize="16"
              color={isChildActive ? "white" : undefined}
              _groupHover={{ color: "white" }}
              as={link.icon}
            />
            {link.name}
          </Flex>
        </Box>
        {isOpen && (
          <Box pl={4} backgroundColor={"bg.emphasized"}>
            {link.children.map((child) => (
              <LinkItem key={child.name} link={child} onClose={onClose} />
            ))}
          </Box>
        )}
      </Box>
    );
  }

  // Leaf node: render as a standard NavItem (internal or external)
  return (
    <NavItem
      icon={link.icon}
      path={link.path}
      external={link.external}
      onClick={onClose}
    >
      {link.name}
    </NavItem>
  );
};

interface NavItemProps extends FlexProps {
  icon?: IconType;
  path?: string;
  external?: boolean;
}

export const NavItem = ({
  icon,
  children,
  path,
  external = false,
  ...rest
}: NavItemProps & PropsWithChildren) => {
  const location = useLocation();
  const isActive = isPathActive(location.pathname, path, external);

  const commonFlex = (
    <Flex
      align="center"
      p={!isBrowser ? 4 : 3}
      borderRadius="lg"
      role="group"
      cursor="pointer"
      bg={isActive ? "blue.900" : undefined}
      color={isActive ? "white" : undefined}
      _hover={{ bg: "orange.600", color: "black" }}
      {...rest}
    >
      {icon && (
        <Icon
          mr="4"
          fontSize="16"
          color={isActive ? "white" : undefined}
          _groupHover={{ color: "white" }}
          as={icon}
        />
      )}
      {children}
    </Flex>
  );

  // Render internal routes with React Router Link, external as anchor
  if (external) {
    return (
      <Box
        as="a"
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        href={path}
        target="_blank"
        rel="noopener noreferrer"
        style={{ textDecoration: "none" }}
      >
        {commonFlex}
      </Box>
    );
  }

  return (
    <Box
      as={RouterLink}
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-expect-error
      to={path || "#"}
      aria-current={isActive ? "page" : undefined}
      style={{ textDecoration: "none" }}
    >
      {commonFlex}
    </Box>
  );
};
